// src/components/sections/Videos.jsx
import React, { useState, useRef } from 'react';
import { useTranslation } from '../../i18n';
import SectionTag from '../common/SectionTag';

const VIDEOS = [
  {
    id: 1,
    src: '/Videos/Vidéo1.mp4',
    title: 'Forage',
    category: 'forage',  // ← Utiliser des clés
    description: 'Aide à une école au Tchad'
  },
  {
    id: 2,
    src: '/Videos/Vidéo2.mp4',
    title: 'Ramadan',
    category: 'ramadan',  // ← Utiliser des clés
    description: 'Distribution de vivres aux familles démunies pendant le Ramadan'
  },
  {
    id: 3,
    src: '/Videos/Vidéo3.mp4',
    title: 'Sacrifice',
    category: 'sacrifice',  // ← Utiliser des clés
    description: 'Célébration du Eid al-Adha avec des familles dans le besoin'
  },
];

const Videos = () => {
  const { t } = useTranslation();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [videoErrors, setVideoErrors] = useState({});
  const [videoLoaded, setVideoLoaded] = useState({});
  const videoRefs = useRef({});

  const getFilteredVideos = () => {
    if (selectedCategory === 'all') return VIDEOS;
    return VIDEOS.filter(video => video.category === selectedCategory);
  };

  const getCategories = () => {
    const categories = ['all'];
    VIDEOS.forEach(video => {
      if (!categories.includes(video.category)) {
        categories.push(video.category);
      }
    });
    return categories;
  };

  const filteredVideos = getFilteredVideos();
  const categories = getCategories();

  // ✅ Fonction pour obtenir le libellé de catégorie traduit
  const getCategoryLabel = (cat) => {
    if (cat === 'all') return t('video_all');
    const key = `video_category_${cat}`;
    return t(key) || cat;
  };

  const handleVideoError = (videoId) => {
    setVideoErrors(prev => ({ ...prev, [videoId]: true }));
  };

  const handleVideoLoaded = (videoId) => {
    setVideoLoaded(prev => ({ ...prev, [videoId]: true }));
  };

  const handlePlayClick = (videoId) => {
    if (videoRefs.current[videoId]) {
      try {
        videoRefs.current[videoId].play();
      } catch (error) {
        console.error('Erreur de lecture:', error);
      }
    }
  };

  return (
    <section className="bg-ofd-slate px-4 md:px-10 py-20 text-white" id="videos">
      <div className="max-w-7xl mx-auto">
        <SectionTag className="text-ofd-yellow border-ofd-yellow">{t('video_tag')}</SectionTag>
        <h2 className="font-playfair text-3xl md:text-4xl font-bold text-white">
          {t('video_title')}
        </h2>
        <p className="text-white/60 max-w-2xl">{t('video_desc')}</p>

        {/* ✅ Filtres par catégorie avec traductions */}
        {categories.length > 1 && (
          <div className="flex flex-wrap gap-2 mt-6 mb-6">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                  selectedCategory === cat
                    ? 'bg-ofd-yellow text-ofd-slate'
                    : 'bg-white/10 text-white/60 hover:bg-white/20'
                }`}
              >
                {getCategoryLabel(cat)}
                <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${
                  selectedCategory === cat
                    ? 'bg-ofd-slate/20 text-ofd-slate'
                    : 'bg-white/10 text-white/40'
                }`}>
                  {cat === 'all' ? VIDEOS.length : VIDEOS.filter(v => v.category === cat).length}
                </span>
              </button>
            ))}
          </div>
        )}

        {filteredVideos.length === 0 ? (
          <div className="text-center py-20 text-white/40 mt-10">
            <div className="max-w-md mx-auto">
              <span className="text-7xl block mb-6">🎬</span>
              <h3 className="text-2xl font-playfair font-bold text-white mb-3">
                {t('video_no_videos')}
              </h3>
              <p className="text-sm text-white/40">
                {t('video_soon')}
              </p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-6">
            {filteredVideos.map((video) => (
              <div
                key={video.id}
                className="bg-white/5 rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 group border border-white/10"
              >
                <div className="relative w-full bg-black" style={{ paddingBottom: '56.25%' }}>
                  {videoErrors[video.id] ? (
                    <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/50 text-white/60">
                      <span className="text-5xl mb-4">🎬</span>
                      <p className="text-sm">{t('video_not_available')}</p>
                      <p className="text-xs text-white/30 mt-2">{t('video_format_not_supported')}</p>
                    </div>
                  ) : (
                    <video
                      ref={el => videoRefs.current[video.id] = el}
                      controls
                      className="absolute top-0 left-0 w-full h-full object-contain bg-black"
                      preload="metadata"
                      onError={() => handleVideoError(video.id)}
                      onLoadedData={() => handleVideoLoaded(video.id)}
                      playsInline
                      webkit-playsinline="true"
                    >
                      <source src={video.src} type="video/mp4" />
                      <source src={video.src} type="video/quicktime" />
                      <p>{t('video_format_not_supported')}</p>
                    </video>
                  )}
                  
                  {!videoLoaded[video.id] && !videoErrors[video.id] && (
                    <div 
                      className="absolute inset-0 flex items-center justify-center cursor-pointer group-hover:opacity-100 transition-opacity duration-300 bg-black/30"
                      onClick={() => handlePlayClick(video.id)}
                    >
                      <div className="w-16 h-16 rounded-full bg-ofd-yellow/80 flex items-center justify-center hover:bg-ofd-yellow transition-colors">
                        <svg className="w-8 h-8 text-ofd-slate ml-1" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M8 5v14l11-7z" />
                        </svg>
                      </div>
                    </div>
                  )}
                </div>
                
                <div className="p-4">
                  <h4 className="font-semibold text-white text-sm mb-1">
                    {video.title || 'Vidéo OFD'}
                  </h4>
                  {video.description && (
                    <p className="text-white/40 text-xs mb-2">
                      {video.description}
                    </p>
                  )}
                  {video.category && (
                    <span className="inline-block bg-ofd-yellow/20 text-ofd-yellow text-xs px-2 py-1 rounded-full">
                      {getCategoryLabel(video.category)}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="mt-8 p-4 bg-white/5 rounded-lg text-center text-white/40 text-sm border border-white/10">
          ℹ️ {t('video_info')}
        </div>
      </div>
    </section>
  );
};

export default Videos;